import { ComplianceCheck, ScoreBreakdown } from '../../utils/types';
import { severityCap } from '@privisy/domain';

export interface VerdictCheckCounts {
  error: number;
  /** Every warning-status check — drives the >=90 band's "minor issue" wording. */
  warning: number;
  /**
   * Warnings that evidence an actual compliance gap (countGapWarnings' output) — drives
   * the "N compliance gaps" wording at score >=70 and the <70 fallback. Falls back to
   * `warning` when omitted, so existing callers keep their current behavior; the >=90
   * band always uses raw `warning` regardless, since "N minor issues to address" is an
   * honest description of an advisory warning, unlike "N compliance gaps" or "no gaps
   * found" (#1174 review of #1132).
   */
  gapWarning?: number;
  /** Failed checks with critical severity — drives the "critical" wording */
  criticalFailures?: number;
  /** Display labels of the sections containing the failing checks */
  failingSections?: string[];
}

function sectionClause(failingSections: string[] | undefined): string {
  if (!failingSections || failingSections.length === 0) return '';
  if (failingSections.length <= 2) return ` (${failingSections.join(', ')})`;
  return ` across ${failingSections.length} audit areas`;
}

/**
 * Count warnings that represent a real compliance gap, for VerdictCheckCounts.gapWarning.
 * `advisory` is the catalog's own flag for a warning that is not itself a finding the
 * customer needs to remediate (CheckCatalogEntry.advisory) — e.g. ".well-known GPC
 * Declaration" is documented as "Never non_compliant, because no CCPA provision requires
 * the file", yet it emits `warning` on almost every scan (absence is the common case).
 * Counting it toward "N compliance gaps requiring attention" overstated the gap count on
 * nearly every 70–89 report (#1132). Deliberately not a severity filter: some info-severity
 * checks' warnings DO evidence an uncounted requirement (#1174 review of #1132).
 */
export function countGapWarnings(checks: Pick<ComplianceCheck, 'status' | 'advisory' | 'confirmApplies'>[]): number {
  // A `confirmApplies` warning (#1657) is a duty the scan cannot observe, which
  // "What to fix" files under "Confirm it applies to you" rather than its
  // warnings count; the gap count follows it so the two agree (#1899).
  return checks.filter((c) => c.status === 'warning' && !c.advisory && !c.confirmApplies).length;
}

export function buildVerdict(score: number, checkCounts: VerdictCheckCounts, hostname: string): string {
  if (score >= 90) {
    if (checkCounts.error > 0) {
      // A failed check landed alongside a high score — that's still a gap
      // the customer needs to address, not a "minor" warning-only issue.
      const gaps = checkCounts.error + checkCounts.warning;
      return `${hostname} meets core CCPA requirements, but has ${gaps} issue${gaps === 1 ? '' : 's'} to address.`;
    }
    if (checkCounts.warning > 0) {
      return `${hostname} meets core CCPA requirements, but has ${checkCounts.warning} minor issue${checkCounts.warning === 1 ? '' : 's'} to address.`;
    }
    return `${hostname} meets all checked CCPA requirements. No compliance gaps were found.`;
  }

  const gapWarning = checkCounts.gapWarning ?? checkCounts.warning;

  if (score >= 70) {
    return `${hostname} passes most checks, but has ${checkCounts.error + gapWarning} compliance gap${(checkCounts.error + gapWarning) === 1 ? '' : 's'} requiring attention.`;
  }

  const critical = checkCounts.criticalFailures ?? 0;
  if (critical > 0) {
    return `${hostname} failed ${critical} critical CCPA requirement${critical === 1 ? '' : 's'}${sectionClause(checkCounts.failingSections)} and is at high risk of non-compliance.`;
  }

  const gaps = checkCounts.error + gapWarning;
  return `${hostname} has ${gaps} compliance gap${gaps === 1 ? '' : 's'}${sectionClause(checkCounts.failingSections)} and needs remediation to meet CCPA requirements.`;
}

export interface ScoreCapInfo {
  /** The cap that bound the score: 49 (critical), 74 (high), 89 (medium), 94 (low), or 99 (warning) */
  cap: number;
  /**
   * 'warning' means an unscored (non-binary), non-advisory warning check bound the score
   * at the soft 99 cap (issue #1237) — distinct from the four failure severities, which
   * bind a `non_compliant` check instead. Consumers should not describe a 'warning' cap
   * as "a requirement failed": nothing failed, one or more checks just could not be
   * assessed as fully compliant.
   */
  severity: 'critical' | 'high' | 'medium' | 'low' | 'warning';
  /** Raw points-based score before capping; null when scoreBreakdown is unavailable */
  uncappedScore: number | null;
}

/**
 * Detect whether the backend's severity hard-cap ladder (critical → 49,
 * high → 74, medium → 89, low → 94, unscored warning → 99 — the
 * severityCapFor ladder shared by calculateDraftScore and
 * calculateScoreBreakdown) bound the displayed score, so the report can
 * disclose why e.g. one critical failure produces "F — 49/100", or why a
 * report otherwise at 100% binary points shows 99.
 *
 * The 99 rung (issue #1237) fires only for a non-binary, non-`advisory`
 * warning — matching severityCapFor's exclusion of `advisory` warnings
 * (helpers.ts), so a satisfied alternate-disclosure waiver (e.g. the ADMT
 * § 7221(b)(1) opt-out/human-review waiver) does not trigger this cap.
 *
 * On legacy reports without a scoreBreakdown the raw score is unknown; we still
 * disclose the cap when the score sits exactly at it (disclosure is the safer
 * default for an audit document).
 */
export function detectScoreCap(
  score: number,
  checks: Pick<ComplianceCheck, 'status' | 'severity' | 'isBinaryCheck' | 'advisory'>[],
  scoreBreakdown?: ScoreBreakdown,
): ScoreCapInfo | null {
  const hit = severityCap(checks);
  if (!hit || score !== hit.cap) return null;
  const { cap, severity } = hit;

  let uncappedScore: number | null = null;
  if (scoreBreakdown && scoreBreakdown.totalPoints > 0) {
    uncappedScore = Math.round((scoreBreakdown.earnedPoints / scoreBreakdown.totalPoints) * 100);
    if (uncappedScore <= cap) return null;
  }

  return { cap, severity, uncappedScore };
}
